import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  Grid,
  Alert,
  Snackbar,
  Paper,
  Avatar,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tab,
  Tabs,
  TablePagination,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuList,
  MenuItem as MuiMenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  LinearProgress,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { apiGet, apiPost, apiPut, apiDelete, apiPatch } from '../../services/api';
import {
  Person,
  Email,
  Phone,
  Search,
  FilterList,
  Delete,
  Visibility,
  MoreVert,
  Download,
  Refresh,
  School,
  CheckCircle,
  Error,
  Close,
  Sort,
  ArrowUpward,
  ArrowDownward,
  Mail,
  Send,
  WhatsApp,
  Edit,
  AttachFile,
  CloudUpload,
} from '@mui/icons-material';

interface StudentRegistration {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  courseCategory: string;
  subCategory: string;
  studyLevel: string;
  studySubLevel?: string;
  studyMode: string;
  experience: string;
  goals: string;
  hearAboutUs: string;
  registrationId?: string;
  status?: string;
  emailSent?: boolean;
  submittedAt?: string;
  createdAt: string;
  lastUpdated?: string;
  paymentStatus?: 'paid' | 'unpaid';
  attendanceStatus?: 'attended' | 'not_attended';
  registeredBy?: { _id: string; firstName: string; lastName: string; email: string } | null;
}

interface FilterState {
  search: string;
  courseCategory: string;
  subCategory: string;
  studyLevel: string;
  studySubLevel: string;
  studyMode: string;
  status: string;
  paymentStatus: string;
  attendanceStatus: string;
  dateRange: string;
  updated: string;
  registeredBy: string;
}

interface SortState {
  field: string;
  direction: 'asc' | 'desc';
}

const StudentRegistrationPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [registrations, setRegistrations] = useState<StudentRegistration[]>([]);
  const [loading, setLoading] = useState(false);
  const [filteredRegistrations, setFilteredRegistrations] = useState<StudentRegistration[]>([]);
  
  const isMobile = useMediaQuery('(max-width:600px)');
  
  // Enhanced state for filters and actions
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    courseCategory: '',
    subCategory: '',
    studyLevel: '',
    studySubLevel: '',
    studyMode: '',
    status: '',
    paymentStatus: '',
    attendanceStatus: '',
    dateRange: '',
    updated: '',
    registeredBy: ''
  });
  
  const [sort, setSort] = useState<SortState>({
    field: 'createdAt',
    direction: 'desc'
  });
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedStudent, setSelectedStudent] = useState<StudentRegistration | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStudentForActions, setSelectedStudentForActions] = useState<StudentRegistration | null>(null);
  const [bulkEmailDialogOpen, setBulkEmailDialogOpen] = useState(false);
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailMessage, setBulkEmailMessage] = useState('');
  const [selectedCourseCategory, setSelectedCourseCategory] = useState('');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [selectedStudentForEmail, setSelectedStudentForEmail] = useState<StudentRegistration | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [availableStudySubLevels, setAvailableStudySubLevels] = useState<string[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<StudentRegistration>>({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [emailAttachments, setEmailAttachments] = useState<File[]>([]);
  const [bulkEmailAttachments, setBulkEmailAttachments] = useState<File[]>([]);
  const [selectedStudentsForBulkEmail, setSelectedStudentsForBulkEmail] = useState<string[]>([]);
  const [bulkEmailSearchQuery, setBulkEmailSearchQuery] = useState('');
  const [bulkEmailFilterStudyLevel, setBulkEmailFilterStudyLevel] = useState('');
  const [bulkEmailFilterStudySubLevel, setBulkEmailFilterStudySubLevel] = useState('');

  // Data fetching
  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const data = await apiGet<any>('/course-registration');
      setRegistrations(data.data || data);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      setErrorMessage('Failed to fetch registrations');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudySubLevels = () => {
    const subLevels = registrations
      .map(reg => reg.studySubLevel)
      .filter((subLevel): subLevel is string => subLevel !== undefined && subLevel !== null && subLevel !== '');
    const uniqueSubLevels = Array.from(new Set(subLevels));
    setAvailableStudySubLevels(uniqueSubLevels);
  };

  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...registrations];

    // Apply search filter
    if (filters.search) {
      filtered = filtered.filter(reg => 
        `${reg.firstName} ${reg.lastName}`.toLowerCase().includes(filters.search.toLowerCase()) ||
        reg.email.toLowerCase().includes(filters.search.toLowerCase()) ||
        reg.phone.includes(filters.search) ||
        reg.courseCategory.toLowerCase().includes(filters.search.toLowerCase()) ||
        reg.subCategory.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    // Apply course category filter
    if (filters.courseCategory) {
      filtered = filtered.filter(reg => reg.courseCategory.toLowerCase().includes(filters.courseCategory.toLowerCase()));
    }

    // Apply subcategory filter
    if (filters.subCategory) {
      filtered = filtered.filter(reg => reg.subCategory.toLowerCase().includes(filters.subCategory.toLowerCase()));
    }

    // Apply study level filter
    if (filters.studyLevel) {
      filtered = filtered.filter(reg => reg.studyLevel.toLowerCase().includes(filters.studyLevel.toLowerCase()));
    }

    // Apply study sub-level filter
    if (filters.studySubLevel) {
      filtered = filtered.filter(reg => (reg.studySubLevel || '').toLowerCase().includes(filters.studySubLevel.toLowerCase()));
    }

    // Apply study mode filter
    if (filters.studyMode) {
      filtered = filtered.filter(reg => reg.studyMode === filters.studyMode);
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(reg => reg.status === filters.status);
    }

    // Apply payment status filter
    if (filters.paymentStatus) {
      filtered = filtered.filter(reg => reg.paymentStatus === filters.paymentStatus);
    }

    // Apply attendance status filter
    if (filters.attendanceStatus) {
      filtered = filtered.filter(reg => reg.attendanceStatus === filters.attendanceStatus);
    }

    // Apply registered-by filter
    if (filters.registeredBy) {
      filtered = filters.registeredBy === 'direct'
        ? filtered.filter(reg => !reg.registeredBy)
        : filtered.filter(reg => reg.registeredBy?._id === filters.registeredBy);
    }

    // Apply date range filter
    if (filters.dateRange) {
      const now = new Date();
      const filterDate = new Date();
      
      switch (filters.dateRange) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          filterDate.setFullYear(now.getFullYear() - 1);
          break;
      }
      
      filtered = filtered.filter(reg => new Date(reg.createdAt) >= filterDate);
    }

    // Filter by updated status
    if (filters.updated) {
      if (filters.updated === 'yes') {
        filtered = filtered.filter(reg => reg.lastUpdated && new Date(reg.lastUpdated) > new Date(reg.createdAt));
      } else if (filters.updated === 'no') {
        filtered = filtered.filter(reg => !reg.lastUpdated || new Date(reg.lastUpdated) <= new Date(reg.createdAt));
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sort.field as keyof StudentRegistration];
      const bValue = b[sort.field as keyof StudentRegistration];
      
      if (aValue === undefined || bValue === undefined) return 0;
      
      let comparison = 0;
      if (aValue < bValue) comparison = -1;
      if (aValue > bValue) comparison = 1;
      
      return sort.direction === 'desc' ? -comparison : comparison;
    });

    setFilteredRegistrations(filtered);
  }, [registrations, filters, sort]);

  useEffect(() => {
    fetchRegistrations();
  }, []);

  useEffect(() => {
    fetchStudySubLevels();
  }, [registrations]);

  // Clear selected students when bulk email filters change
  useEffect(() => {
    if (bulkEmailDialogOpen) {
      setSelectedStudentsForBulkEmail([]);
    }
  }, [bulkEmailFilterStudyLevel, bulkEmailFilterStudySubLevel, bulkEmailSearchQuery, bulkEmailDialogOpen]);

  // Filter handlers
  const handleFilterChange = (field: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSort = (field: string) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      courseCategory: '',
      subCategory: '',
      studyLevel: '',
      studySubLevel: '',
      studyMode: '',
      status: '',
      paymentStatus: '',
      attendanceStatus: '',
      dateRange: '',
      updated: ''
    });
  };

  // Action handlers
  const handleViewStudent = (student: StudentRegistration) => {
    setSelectedStudent(student);
    setViewDialogOpen(true);
  };

  const handleDeleteStudent = (student: StudentRegistration) => {
    setSelectedStudent(student);
    setDeleteDialogOpen(true);
  };

  const handleEditStudent = (student: StudentRegistration) => {
    setSelectedStudent(student);
    // Convert to arrays for CPA courses (multi-select), keep as strings for others
    const isCPACourse = student.subCategory?.includes('CPA') || student.subCategory?.includes('KASNEB') || student.subCategory?.includes('ICPAR');
    const studyLevelArray = isCPACourse && student.studyLevel ? student.studyLevel.split(',').map(s => s.trim()).filter(Boolean) : student.studyLevel;
    const studySubLevelArray = isCPACourse && student.studySubLevel ? student.studySubLevel.split(',').map(s => s.trim()).filter(Boolean) : student.studySubLevel;

    setEditFormData({
      courseCategory: student.courseCategory,
      subCategory: student.subCategory,
      studyLevel: studyLevelArray as any,
      studySubLevel: studySubLevelArray as any,
      studyMode: student.studyMode,
      paymentStatus: student.paymentStatus,
      attendanceStatus: student.attendanceStatus,
      status: student.status
    });
    setEditDialogOpen(true);
  };

  // Handle array toggle for multi-select fields (studyLevel, studySubLevel)
  const handleArrayToggle = (field: 'studyLevel' | 'studySubLevel', value: string) => {
    const currentArray = Array.isArray(editFormData[field]) ? (editFormData[field] as string[]) : [];
    const isKasnebCpa = editFormData.subCategory?.includes('KASNEB');
    const isIcparCpa = editFormData.subCategory?.includes('ICPAR');
    const isCPACourse = isKasnebCpa || isIcparCpa || editFormData.subCategory?.includes('CPA');

    if (currentArray.includes(value)) {
      // Deselecting
      const newArray = currentArray.filter(item => item !== value);

      // If deselecting a sub-level for CPA courses, check if parent level should be deselected
      if (field === 'studySubLevel' && isCPACourse) {
        const levelData = isKasnebCpa ? kasnebCpaStudyLevels : icparCpaStudyLevels;
        for (const [level, subLevels] of Object.entries(levelData)) {
          if (subLevels.includes(value)) {
            // Check if any other sub-levels in this level are still selected
            const remainingSubLevels = newArray.filter(sub =>
              levelData[level].includes(sub)
            );
            if (remainingSubLevels.length === 0) {
              // No more sub-levels in this level, deselect the parent level
              const currentStudyLevel = Array.isArray(editFormData.studyLevel) ? editFormData.studyLevel : [];
              setEditFormData({
                ...editFormData,
                [field]: newArray,
                studyLevel: currentStudyLevel.filter(l => l !== level)
              });
              return;
            }
            break;
          }
        }
      }

      setEditFormData({ ...editFormData, [field]: newArray });
    } else {
      // Selecting
      const newArray = [...currentArray, value];

      // If selecting a sub-level for CPA courses, automatically add the parent level
      if (field === 'studySubLevel' && isCPACourse) {
        const levelData = isKasnebCpa ? kasnebCpaStudyLevels : icparCpaStudyLevels;
        for (const [level, subLevels] of Object.entries(levelData)) {
          if (subLevels.includes(value)) {
            const currentStudyLevel = Array.isArray(editFormData.studyLevel) ? editFormData.studyLevel : [];
            if (!currentStudyLevel.includes(level)) {
              setEditFormData({
                ...editFormData,
                [field]: newArray,
                studyLevel: [...currentStudyLevel, level]
              });
              return;
            }
            break;
          }
        }
      }

      setEditFormData({ ...editFormData, [field]: newArray });
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedStudent) return;

    setSavingEdit(true);
    try {
      // Convert arrays to comma-separated strings for saving (matching homepage format)
      const isCPACourse = editFormData.subCategory?.includes('CPA') || editFormData.subCategory?.includes('KASNEB') || editFormData.subCategory?.includes('ICPAR');
      const saveData = {
        ...editFormData,
        studyLevel: Array.isArray(editFormData.studyLevel)
          ? editFormData.studyLevel.join(', ')
          : editFormData.studyLevel,
        studySubLevel: Array.isArray(editFormData.studySubLevel)
          ? editFormData.studySubLevel.join(', ')
          : editFormData.studySubLevel
      };

      await apiPut(`/course-registration/${selectedStudent._id}`, saveData);
      setSuccessMessage('Student registration updated successfully');
      fetchRegistrations();
      setEditDialogOpen(false);
      setSelectedStudent(null);
    } catch (error) {
      setErrorMessage('Failed to update student registration');
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedStudent) return;

    try {
      await apiDelete(`/course-registration/${selectedStudent._id}`);
      setSuccessMessage('Student registration deleted successfully');
      fetchRegistrations();
    } catch (error) {
      setErrorMessage('Failed to delete student registration');
    }

    setDeleteDialogOpen(false);
    setSelectedStudent(null);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, student: StudentRegistration) => {
    setAnchorEl(event.currentTarget);
    setSelectedStudentForActions(student);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStudentForActions(null);
  };

  // Handle click on phone number to send email
  const handlePhoneClick = (student: StudentRegistration) => {
    setSelectedStudentForEmail(student);
    setEmailSubject('Regarding your course registration');
    setEmailMessage(`Dear ${student.firstName} ${student.lastName},\n\nI hope this email finds you well. I am writing regarding your registration for ${student.courseCategory} - ${student.subCategory}.\n\nPlease let me know if you have any questions.\n\nBest regards`);
    setEmailDialogOpen(true);
  };

  // Handle file selection for individual email
  const handleEmailAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setEmailAttachments(prev => [...prev, ...newFiles]);
    }
  };

  // Handle file removal for individual email
  const handleRemoveEmailAttachment = (index: number) => {
    setEmailAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Handle file selection for bulk email
  const handleBulkEmailAttachmentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setBulkEmailAttachments(prev => [...prev, ...newFiles]);
    }
  };

  // Handle file removal for bulk email
  const handleRemoveBulkEmailAttachment = (index: number) => {
    setBulkEmailAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Send individual email
  const sendIndividualEmail = async () => {
    if (!selectedStudentForEmail) return;

    setSendingEmail(true);
    try {
      const formData = new FormData();
      formData.append('subject', emailSubject);
      formData.append('message', emailMessage);
      
      console.log('📧 Sending email to:', selectedStudentForEmail.email);
      console.log('📎 Attachments to send:', emailAttachments.length);
      emailAttachments.forEach((file, i) => {
        console.log(`  File ${i + 1}: ${file.name}, Size: ${file.size} bytes, Type: ${file.type}`);
      });
      
      emailAttachments.forEach((file) => {
        formData.append('attachments', file);
      });

      await apiPost(`/course-registration/${selectedStudentForEmail._id}/email`, formData);
      setSuccessMessage('Email sent successfully');
      setEmailDialogOpen(false);
      setEmailSubject('');
      setEmailMessage('');
      setEmailAttachments([]);
      setSelectedStudentForEmail(null);
    } catch (error) {
      setErrorMessage('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Handle bulk email to students in same course category
  const handleBulkEmail = (courseCategory: string) => {
    setSelectedCourseCategory(courseCategory);
    // Reset filters and selections when opening dialog
    setBulkEmailFilterStudyLevel('');
    setBulkEmailFilterStudySubLevel('');
    setBulkEmailSearchQuery('');
    setSelectedStudentsForBulkEmail([]);
    setBulkEmailDialogOpen(true);
  };

  const sendBulkEmail = async () => {
    if (selectedStudentsForBulkEmail.length === 0) {
      setErrorMessage('Please select at least one student');
      return;
    }

    setSendingEmail(true);
    try {
      const formData = new FormData();
      formData.append('subject', bulkEmailSubject);
      formData.append('message', bulkEmailMessage);
      
      // Send selected student IDs
      selectedStudentsForBulkEmail.forEach(studentId => {
        formData.append('studentIds', studentId);
      });
      
      console.log('📧 Sending BULK email to', selectedStudentsForBulkEmail.length, 'students');
      console.log('📎 Bulk attachments to send:', bulkEmailAttachments.length);
      bulkEmailAttachments.forEach((file, i) => {
        console.log(`  File ${i + 1}: ${file.name}, Size: ${file.size} bytes, Type: ${file.type}`);
      });
      
      bulkEmailAttachments.forEach((file) => {
        formData.append('attachments', file);
      });

      const response = await apiPost('/course-registration/bulk-email', formData);
      
      console.log('✅ Bulk email response:', response);
      setSuccessMessage('Bulk email sent successfully');
      setBulkEmailDialogOpen(false);
      setBulkEmailSubject('');
      setBulkEmailMessage('');
      setBulkEmailAttachments([]);
      setSelectedStudentsForBulkEmail([]);
      setSelectedCourseCategory('');
    } catch (error) {
      setErrorMessage('Failed to send bulk email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Export functionality
  const exportToCSV = () => {
    const csv = [
      ['Name', 'Email', 'Phone', 'Course Category', 'Subcategory', 'Study Level', 'Study Mode', 'Payment Status', 'Attendance Status', 'Registration Date'],
      ...filteredRegistrations.map(reg => [
        `${reg.firstName} ${reg.lastName}`,
        reg.email,
        reg.phone,
        reg.courseCategory,
        reg.subCategory,
        reg.studyLevel,
        reg.studyMode,
        reg.paymentStatus || 'unpaid',
        reg.attendanceStatus || 'not_attended',
        new Date(reg.createdAt).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_registrations.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // PDF export functionality
  const exportToPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setErrorMessage('Please allow popups to download PDF');
      return;
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Student Registrations</title>
        <style>
          @media print {
            body { margin: 0; padding: 20px; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            thead { display: table-header-group; }
            tfoot { display: table-footer-group; }
            .footer { position: fixed; bottom: 0; left: 0; right: 0; }
          }
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #333; text-align: center; page-break-after: avoid; }
          .header-info { page-break-after: avoid; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }
          th, td { border: 1px solid #ddd; padding: 6px; text-align: left; word-wrap: break-word; }
          th { background-color: #f2f2f2; font-weight: bold; font-size: 10px; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .status-paid { color: green; font-weight: bold; }
          .status-unpaid { color: red; font-weight: bold; }
          .status-attended { color: blue; font-weight: bold; }
          .status-not-attended { color: orange; font-weight: bold; }
          .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #666; page-break-before: always; }
        </style>
      </head>
      <body>
        <h1>Student Registration Report</h1>
        <div class="header-info">
          <p><strong>Total Students:</strong> ${filteredRegistrations.length}</p>
          <p><strong>Generated on:</strong> ${new Date().toLocaleString()}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width: 12%;">Name</th>
              <th style="width: 15%;">Email</th>
              <th style="width: 10%;">Phone</th>
              <th style="width: 10%;">Course Category</th>
              <th style="width: 12%;">Subcategory</th>
              <th style="width: 10%;">Study Level</th>
              <th style="width: 12%;">Study Sub-Level</th>
              <th style="width: 8%;">Study Mode</th>
              <th style="width: 6%;">Payment</th>
              <th style="width: 8%;">Attendance</th>
              <th style="width: 7%;">Date</th>
            </tr>
          </thead>
          <tbody>
            ${filteredRegistrations.map(reg => `
              <tr>
                <td>${reg.firstName} ${reg.lastName}</td>
                <td>${reg.email}</td>
                <td>${reg.phone}</td>
                <td>${reg.courseCategory}</td>
                <td>${reg.subCategory}</td>
                <td>${reg.studyLevel}</td>
                <td>${reg.studySubLevel || '-'}</td>
                <td>${reg.studyMode}</td>
                <td class="${reg.paymentStatus === 'paid' ? 'status-paid' : 'status-unpaid'}">${reg.paymentStatus || 'unpaid'}</td>
                <td class="${reg.attendanceStatus === 'attended' ? 'status-attended' : 'status-not-attended'}">${reg.attendanceStatus || 'not_attended'}</td>
                <td>${new Date(reg.createdAt).toLocaleDateString()}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">
          <p>Excellence Coaching Hub - Student Registration Report</p>
          <p>Page <span class="page-number"></span> of <span class="page-count"></span></p>
        </div>
        <script>
          window.onbeforeprint = function() {
            document.querySelectorAll('.page-number').forEach((el, i) => {
              el.textContent = i + 1;
            });
            document.querySelectorAll('.page-count').forEach(el => {
              el.textContent = Math.ceil(document.body.offsetHeight / window.innerHeight);
            });
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  // Update payment status
  const updatePaymentStatus = async (studentId: string, status: 'paid' | 'unpaid') => {
    try {
      await apiPatch(`/course-registration/${studentId}/payment-status`, { paymentStatus: status });
      setSuccessMessage(`Payment status updated to ${status}`);
      fetchRegistrations();
    } catch (error) {
      setErrorMessage('Failed to update payment status');
    }
  };

  // Update attendance status
  const updateAttendanceStatus = async (studentId: string, status: 'attended' | 'not_attended') => {
    try {
      await apiPatch(`/course-registration/${studentId}/attendance-status`, { attendanceStatus: status });
      setSuccessMessage(`Attendance status updated to ${status}`);
      fetchRegistrations();
    } catch (error) {
      setErrorMessage('Failed to update attendance status');
    }
  };

  const courseCategories = [
    {
      title: "Professional Coaching",
      description: "Data Analytics, CPA, ACCA, PMP, Leadership and Management, and more",
      color: "#ff6b6b",
      subCategories: [
        "Data Analytics & Business Intelligence",
        "CPA (Certified Public Accountant)",
        "ACCA (Association of Chartered Certified Accountants)",
        "PMP (Project Management Professional)",
        "Leadership Development",
        "Management Skills",
        "Strategic Planning",
      ]
    },
    {
      title: "Academic Coaching",
      description: "Mathematics, Sciences, Languages, and more",
      color: "#4ecdc4",
      subCategories: [
        "Mathematics & Statistics",
        "Physics & Chemistry",
        "Biology & Life Sciences",
        "English Literature & Writing",
        "Foreign Languages",
        "History & Social Studies",
        "Computer Science & IT",
      ]
    },
    {
      title: "Personal Development",
      description: "Communication, Time Management, Leadership, and more",
      color: "#f7b731",
      subCategories: [
        "Communication Skills",
        "Time Management",
        "Leadership Training",
        "Public Speaking",
        "Emotional Intelligence",
        "Stress Management",
        "Career Development",
      ]
    },
    {
      title: "Health & Wellness",
      description: "Nutrition, Fitness, Mental Health, and more",
      color: "#5f27cd",
      subCategories: [
        "Nutrition & Diet Planning",
        "Fitness Training",
        "Mental Health & Wellness Support",
        "Emotional Intelligence Training",
        "Family Counseling & Therapy",
        "Stress Management Techniques",
      ]
    }
  ];

  // Render statistics section - dynamic based on filtered results
  const renderStatistics = () => {
    const isFiltered = filteredRegistrations.length !== registrations.length;
    const dataSource = isFiltered ? filteredRegistrations : registrations;

    const totalStudents = dataSource.length;
    const paidStudents = dataSource.filter(r => r.paymentStatus === 'paid').length;
    const unpaidStudents = dataSource.filter(r => r.paymentStatus === 'unpaid').length;
    const attendedStudents = dataSource.filter(r => r.attendanceStatus === 'attended').length;
    const notAttendedStudents = dataSource.filter(r => r.attendanceStatus === 'not_attended').length;
    const updatedStudents = dataSource.filter(r => r.lastUpdated && new Date(r.lastUpdated) > new Date(r.createdAt)).length;
    const notUpdatedStudents = dataSource.filter(r => !r.lastUpdated || new Date(r.lastUpdated) <= new Date(r.createdAt)).length;

    // Count by course category
    const categoryStats = dataSource.reduce((acc, reg) => {
      acc[reg.courseCategory] = (acc[reg.courseCategory] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Count by study level
    const levelStats = dataSource.reduce((acc, reg) => {
      acc[reg.studyLevel] = (acc[reg.studyLevel] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const statCards = [
      { value: totalStudents, label: isFiltered ? 'Filtered Students' : 'Total Students', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' },
      { value: paidStudents, label: 'Paid Students', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' },
      { value: attendedStudents, label: 'Attended', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' },
      { value: updatedStudents, label: 'Updated', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' },
      { value: notUpdatedStudents, label: 'Not Updated', gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' },
      { value: Object.keys(categoryStats).length, label: 'Course Categories', gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', color: '#333' },
    ];

    return (
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        {isFiltered && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Showing filtered statistics ({filteredRegistrations.length} of {registrations.length} total)
          </Typography>
        )}
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          {statCards.map(stat => (
            <Grid item xs={6} sm={4} md={2} key={stat.label}>
              <Card sx={{ background: stat.gradient, color: stat.color, height: '100%' }}>
                <CardContent sx={{ p: { xs: 1.25, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 2 } } }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.4rem', sm: '2rem', md: '2.125rem' } }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.7rem', sm: '0.875rem' }, lineHeight: 1.3 }}>
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // Render filters section
  const renderFilters = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
            <FilterList />
            Filters &amp; Search
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredRegistrations.length} of {registrations.length} registrations
          </Typography>
        </Box>

        {/* Search - own row, full width */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, email, phone, registration ID..."
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
          }}
          sx={{ mb: 2.5 }}
        />

        {/* Dropdown filters - wrap freely, never shrink below a legible width */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 170 }, flex: { xs: '1 1 100%', sm: '0 1 170px' } }}>
            <InputLabel>Payment</InputLabel>
            <Select
              value={filters.paymentStatus}
              onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
              label="Payment"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 170 }, flex: { xs: '1 1 100%', sm: '0 1 170px' } }}>
            <InputLabel>Attendance</InputLabel>
            <Select
              value={filters.attendanceStatus}
              onChange={(e) => handleFilterChange('attendanceStatus', e.target.value)}
              label="Attendance"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="attended">Attended</MenuItem>
              <MenuItem value="not_attended">Not Attended</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 190 }, flex: { xs: '1 1 100%', sm: '0 1 190px' } }}>
            <InputLabel>Registered By</InputLabel>
            <Select
              value={filters.registeredBy}
              onChange={(e) => handleFilterChange('registeredBy', e.target.value)}
              label="Registered By"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="direct">Direct/Web</MenuItem>
              {Array.from(
                new Map(
                  registrations
                    .filter(reg => reg.registeredBy)
                    .map(reg => [reg.registeredBy!._id, reg.registeredBy!])
                ).values()
              ).map(fp => (
                <MenuItem key={fp._id} value={fp._id}>{fp.firstName} {fp.lastName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 190 }, flex: { xs: '1 1 100%', sm: '0 1 190px' } }}>
            <InputLabel>Course Category</InputLabel>
            <Select
              value={filters.courseCategory}
              onChange={(e) => {
                const newCategory = e.target.value;
                setFilters(prev => ({
                  ...prev,
                  courseCategory: newCategory,
                  subCategory: '', // Reset subcategory when category changes
                  studyLevel: '',
                  studySubLevel: ''
                }));
              }}
              label="Course Category"
            >
              <MenuItem value="">All Categories</MenuItem>
              {editCourseCategories.map(cat => (
                <MenuItem key={cat.title} value={cat.title}>{cat.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 190 }, flex: { xs: '1 1 100%', sm: '0 1 190px' } }}>
            <InputLabel>Subcategory</InputLabel>
            <Select
              value={filters.subCategory}
              onChange={(e) => {
                const newSubCategory = e.target.value;
                setFilters(prev => ({
                  ...prev,
                  subCategory: newSubCategory,
                  studyLevel: '',
                  studySubLevel: ''
                }));
              }}
              label="Subcategory"
            >
              <MenuItem value="">All Subcategories</MenuItem>
              {(filters.courseCategory
                ? (editCourseCategories.find(c => c.title === filters.courseCategory)?.subCategories || [])
                : editCourseCategories.flatMap(c => c.subCategories)
              ).map(subCat => (
                <MenuItem key={subCat} value={subCat}>{subCat}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 190 }, flex: { xs: '1 1 100%', sm: '0 1 190px' } }}>
            <InputLabel>Study Level</InputLabel>
            <Select
              value={filters.studyLevel}
              onChange={(e) => handleFilterChange('studyLevel', e.target.value)}
              label="Study Level"
            >
                <MenuItem value="">All Levels</MenuItem>
                {(() => {
                  const isKasnebCpa = filters.subCategory?.includes('KASNEB');
                  const isIcparCpa = filters.subCategory?.includes('ICPAR');
                  const isCPACourse = isKasnebCpa || isIcparCpa || filters.subCategory?.includes('CPA');

                  if (isKasnebCpa) {
                    return [
                      <MenuItem key="Foundation Level" value="Foundation Level">Foundation Level</MenuItem>,
                      <MenuItem key="Intermediate Level" value="Intermediate Level">Intermediate Level</MenuItem>,
                      <MenuItem key="Advanced Level - Compulsory Papers" value="Advanced Level - Compulsory Papers">Advanced Level - Compulsory Papers</MenuItem>,
                      <MenuItem key="Advanced Level - Specialisation Papers" value="Advanced Level - Specialisation Papers">Advanced Level - Specialisation Papers</MenuItem>,
                      <MenuItem key="Advanced Level - Practical / Professional Components" value="Advanced Level - Practical / Professional Components">Advanced Level - Practical / Professional</MenuItem>,
                    ];
                  } else if (isIcparCpa || isCPACourse) {
                    return [
                      <MenuItem key="Technical Level" value="Technical Level">Technical Level</MenuItem>,
                      <MenuItem key="Operational Level" value="Operational Level">Operational Level</MenuItem>,
                      <MenuItem key="Strategic Level" value="Strategic Level">Strategic Level</MenuItem>,
                      <MenuItem key="Professional Level" value="Professional Level">Professional Level</MenuItem>,
                    ];
                  } else if (filters.subCategory) {
                    return [
                      <MenuItem key="Beginner" value="Beginner">Beginner</MenuItem>,
                      <MenuItem key="Intermediate" value="Intermediate">Intermediate</MenuItem>,
                      <MenuItem key="Advanced" value="Advanced">Advanced</MenuItem>,
                      <MenuItem key="Professional" value="Professional">Professional</MenuItem>,
                    ];
                  }
                  // No subcategory selected — show all possible levels
                  return [
                    <MenuItem key="Foundation Level" value="Foundation Level">Foundation Level</MenuItem>,
                    <MenuItem key="Intermediate Level" value="Intermediate Level">Intermediate Level</MenuItem>,
                    <MenuItem key="Advanced Level - Compulsory Papers" value="Advanced Level - Compulsory Papers">Advanced Level - Compulsory Papers</MenuItem>,
                    <MenuItem key="Advanced Level - Specialisation Papers" value="Advanced Level - Specialisation Papers">Advanced Level - Specialisation Papers</MenuItem>,
                    <MenuItem key="Advanced Level - Practical / Professional Components" value="Advanced Level - Practical / Professional Components">Advanced Level - Practical / Professional</MenuItem>,
                    <MenuItem key="Technical Level" value="Technical Level">Technical Level</MenuItem>,
                    <MenuItem key="Operational Level" value="Operational Level">Operational Level</MenuItem>,
                    <MenuItem key="Strategic Level" value="Strategic Level">Strategic Level</MenuItem>,
                    <MenuItem key="Professional Level" value="Professional Level">Professional Level</MenuItem>,
                    <MenuItem key="Beginner" value="Beginner">Beginner</MenuItem>,
                    <MenuItem key="Intermediate" value="Intermediate">Intermediate</MenuItem>,
                    <MenuItem key="Advanced" value="Advanced">Advanced</MenuItem>,
                    <MenuItem key="Professional" value="Professional">Professional</MenuItem>,
                  ];
                })()}
              </Select>
            </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 190 }, flex: { xs: '1 1 100%', sm: '0 1 190px' } }}>
            <InputLabel>Study Sub-Level</InputLabel>
            <Select
              value={filters.studySubLevel}
              onChange={(e) => handleFilterChange('studySubLevel', e.target.value)}
              label="Study Sub-Level"
            >
              <MenuItem value="">All Sub-Levels</MenuItem>
                {(() => {
                  const isKasnebCpa = filters.subCategory?.includes('KASNEB');
                  const isIcparCpa = filters.subCategory?.includes('ICPAR');

                  if (isKasnebCpa && filters.studyLevel) {
                    const kasnebSubLevels: Record<string, string[]> = {
                      "Foundation Level": ["CA11: Financial Accounting", "CA12: Communication Skills", "CA13: Introduction to Law and Governance", "CA14: Economics", "CA15: Quantitative Analysis", "CA16: Information Communication Technology"],
                      "Intermediate Level": ["CA21: Company Law", "CA22: Financial Management", "CA23: Financial Reporting and Analysis", "CA24: Auditing and Assurance", "CA25: Management Accounting", "CA26: Public Finance and Taxation"],
                      "Advanced Level - Compulsory Papers": ["CA31: Leadership and Management", "CA32: Advanced Financial Reporting and Analysis", "CA33: Advanced Financial Management", "CA34S3: Advanced Management Accounting"],
                      "Advanced Level - Specialisation Papers": ["CA34S1: Advanced Taxation", "CA34S2: Advanced Auditing and Assurance", "CA34S4: Advanced Public Financial Management"],
                      "Advanced Level - Practical / Professional Components": ["CA35P: Business Data Analytics (Practical Paper)", "CA36WE: Workshop on Ethics", "CA37WP: Workshop on Work Simulation"]
                    };
                    return kasnebSubLevels[filters.studyLevel]?.map(sub => (
                      <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                    ));
                  } else if (isKasnebCpa && !filters.studyLevel) {
                    const allKasnebSubLevels = ["CA11: Financial Accounting", "CA12: Communication Skills", "CA13: Introduction to Law and Governance", "CA14: Economics", "CA15: Quantitative Analysis", "CA16: Information Communication Technology", "CA21: Company Law", "CA22: Financial Management", "CA23: Financial Reporting and Analysis", "CA24: Auditing and Assurance", "CA25: Management Accounting", "CA26: Public Finance and Taxation", "CA31: Leadership and Management", "CA32: Advanced Financial Reporting and Analysis", "CA33: Advanced Financial Management", "CA34S3: Advanced Management Accounting", "CA34S1: Advanced Taxation", "CA34S2: Advanced Auditing and Assurance", "CA34S4: Advanced Public Financial Management", "CA35P: Business Data Analytics (Practical Paper)", "CA36WE: Workshop on Ethics", "CA37WP: Workshop on Work Simulation"];
                    return allKasnebSubLevels.map(sub => <MenuItem key={sub} value={sub}>{sub}</MenuItem>);
                  } else if ((isIcparCpa || filters.subCategory?.includes('CPA')) && filters.studyLevel) {
                    const icparSubLevels: Record<string, string[]> = {
                      "Technical Level": ["Financial Accounting", "Management Accounting", "Taxation", "Audit & Assurance"],
                      "Operational Level": ["Law, Ethics & Governance", "Digital Finance", "Financial Management", "Financial Reporting", "Advanced Taxation"],
                      "Strategic Level": ["Advanced Audit & Assurance", "Strategic Management", "Advanced Financial Reporting", "Advanced Public Financial Management (public sector pathway)", "Managing Performance in the Public Sector (public sector pathway)", "Managing Business Performance (private sector pathway)", "Advanced Financial Management (private sector pathway)"],
                      "Professional Level": ["Test of Professional Competence", "Choose Private Sector", "Choose Public Sector"]
                    };
                    return icparSubLevels[filters.studyLevel]?.map(sub => (
                      <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                    ));
                  } else if ((isIcparCpa || filters.subCategory?.includes('CPA')) && !filters.studyLevel) {
                    const allIcparSubLevels = ["Financial Accounting", "Management Accounting", "Taxation", "Audit & Assurance", "Law, Ethics & Governance", "Digital Finance", "Financial Management", "Financial Reporting", "Advanced Taxation", "Advanced Audit & Assurance", "Strategic Management", "Advanced Financial Reporting", "Advanced Public Financial Management (public sector pathway)", "Managing Performance in the Public Sector (public sector pathway)", "Managing Business Performance (private sector pathway)", "Advanced Financial Management (private sector pathway)", "Test of Professional Competence", "Choose Private Sector", "Choose Public Sector"];
                    return allIcparSubLevels.map(sub => <MenuItem key={sub} value={sub}>{sub}</MenuItem>);
                  } else if (!filters.subCategory) {
                    const allSubLevels = Array.from(new Set(availableStudySubLevels));
                    return allSubLevels.map(sub => <MenuItem key={sub} value={sub}>{sub}</MenuItem>);
                  }
                  return null;
                })()}
              </Select>
            </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 }, flex: { xs: '1 1 100%', sm: '0 1 160px' } }}>
            <InputLabel>Study Mode</InputLabel>
            <Select
              value={filters.studyMode}
              onChange={(e) => handleFilterChange('studyMode', e.target.value)}
              label="Study Mode"
            >
              <MenuItem value="">All Modes</MenuItem>
              <MenuItem value="Online">Online</MenuItem>
              <MenuItem value="Offline/In-Person">Offline/In-Person</MenuItem>
              <MenuItem value="Hybrid">Hybrid</MenuItem>
              <MenuItem value="Self-Paced">Self-Paced</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 }, flex: { xs: '1 1 100%', sm: '0 1 150px' } }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={filters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
              label="Date Range"
            >
              <MenuItem value="">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">Last Week</MenuItem>
              <MenuItem value="month">Last Month</MenuItem>
              <MenuItem value="year">Last Year</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 130 }, flex: { xs: '1 1 100%', sm: '0 1 130px' } }}>
            <InputLabel>Updated</InputLabel>
            <Select
              value={filters.updated}
              onChange={(e) => handleFilterChange('updated', e.target.value)}
              label="Updated"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={clearFilters}
            sx={{ height: 40, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            Clear Filters
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  // Render enhanced student table - desktop optimized
  const renderStudentTable = () => (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: 'calc(100vh - 140px)',
        overflow: 'auto',
        '& .MuiTableHead-root': {
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: { xs: 1, md: 2 }, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
          Student Registrations ({filteredRegistrations.length})
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={exportToCSV}
            disabled={filteredRegistrations.length === 0}
          >
            CSV
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Download />}
            onClick={exportToPDF}
            disabled={filteredRegistrations.length === 0}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={fetchRegistrations}
          >
            Refresh
          </Button>
          {filters.courseCategory && (
            <Button
              variant="contained"
              size="small"
              startIcon={<Send />}
              onClick={() => handleBulkEmail(filters.courseCategory)}
              color="primary"
            >
              Email All
            </Button>
          )}
        </Stack>
      </Box>
      <Table stickyHeader size="small" sx={{ minWidth: 1400 }}>
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', py: 1.5 } }}>
            <TableCell sx={{ minWidth: 180, width: 180 }}>
              <Button
                size="small"
                onClick={() => handleSort('firstName')}
                endIcon={sort.field === 'firstName' ? (sort.direction === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />) : <Sort fontSize="small" />}
                sx={{ color: 'white', fontWeight: 'bold', textTransform: 'none', fontSize: '0.875rem' }}
              >
                Name
              </Button>
            </TableCell>
            <TableCell sx={{ minWidth: 200, width: 200 }}>
              <Button
                size="small"
                onClick={() => handleSort('email')}
                endIcon={sort.field === 'email' ? (sort.direction === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />) : <Sort fontSize="small" />}
                sx={{ color: 'white', fontWeight: 'bold', textTransform: 'none', fontSize: '0.875rem' }}
              >
                Email
              </Button>
            </TableCell>
            <TableCell sx={{ minWidth: 130, width: 130, fontWeight: 'bold', fontSize: '0.875rem' }}>Phone</TableCell>
            <TableCell sx={{ minWidth: 160, width: 160 }}>
              <Button
                size="small"
                onClick={() => handleSort('courseCategory')}
                endIcon={sort.field === 'courseCategory' ? (sort.direction === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />) : <Sort fontSize="small" />}
                sx={{ color: 'white', fontWeight: 'bold', textTransform: 'none', fontSize: '0.875rem' }}
              >
                Category
              </Button>
            </TableCell>
            <TableCell sx={{ minWidth: 140, width: 140, fontWeight: 'bold', fontSize: '0.875rem' }}>Subcategory</TableCell>
            <TableCell sx={{ minWidth: 100, width: 100, fontWeight: 'bold', fontSize: '0.875rem' }}>Level</TableCell>
            <TableCell sx={{ minWidth: 110, width: 110, fontWeight: 'bold', fontSize: '0.875rem' }}>Sub-Level</TableCell>
            <TableCell sx={{ minWidth: 90, width: 90, fontWeight: 'bold', fontSize: '0.875rem' }}>Mode</TableCell>
            <TableCell sx={{ minWidth: 100, width: 100, fontWeight: 'bold', fontSize: '0.875rem' }}>Payment</TableCell>
            <TableCell sx={{ minWidth: 100, width: 100, fontWeight: 'bold', fontSize: '0.875rem' }}>Attendance</TableCell>
            <TableCell sx={{ minWidth: 140, width: 140, fontWeight: 'bold', fontSize: '0.875rem' }}>Registered By</TableCell>
            <TableCell sx={{ minWidth: 120, width: 120 }}>
              <Button
                size="small"
                onClick={() => handleSort('createdAt')}
                endIcon={sort.field === 'createdAt' ? (sort.direction === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />) : <Sort fontSize="small" />}
                sx={{ color: 'white', fontWeight: 'bold', textTransform: 'none', fontSize: '0.875rem' }}
              >
                Date
              </Button>
            </TableCell>
            <TableCell sx={{ minWidth: 80, width: 80, fontWeight: 'bold', fontSize: '0.875rem' }}>Updated</TableCell>
            <TableCell sx={{ minWidth: 90, width: 90, fontWeight: 'bold', fontSize: '0.875rem' }}>Email</TableCell>
            <TableCell sx={{ minWidth: 60, width: 60, fontWeight: 'bold', fontSize: '0.875rem' }}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredRegistrations
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((reg) => (
            <TableRow key={reg._id} hover sx={{ '& td': { py: 1, px: 1.5 } }}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
                    {reg.firstName.charAt(0)}{reg.lastName.charAt(0)}
                  </Avatar>
                  <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 120 }}>
                    {reg.firstName} {reg.lastName}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Tooltip title={reg.email} arrow>
                  <Box 
                    sx={{ 
                      cursor: 'pointer', 
                      color: 'primary.main',
                      '&:hover': { textDecoration: 'underline' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      maxWidth: 180,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    onClick={() => handlePhoneClick(reg)}
                  >
                    <Mail fontSize="small" />
                    <Typography variant="body2" noWrap>{reg.email}</Typography>
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title={`WhatsApp: ${reg.phone}`} arrow>
                  <Box 
                    sx={{ 
                      cursor: 'pointer', 
                      color: 'success.main',
                      '&:hover': { textDecoration: 'underline' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      maxWidth: 120,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    onClick={() => {
                      const whatsappUrl = `https://wa.me/${reg.phone.replace(/[^0-9]/g, '')}`;
                      window.open(whatsappUrl, '_blank');
                    }}
                  >
                    <WhatsApp fontSize="small" />
                    <Typography variant="body2" noWrap>{reg.phone}</Typography>
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title={reg.courseCategory} arrow>
                    <Chip 
                      label={reg.courseCategory.length > 15 ? reg.courseCategory.substring(0, 15) + '...' : reg.courseCategory} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 24, maxWidth: 120 }}
                    />
                  </Tooltip>
                  <IconButton
                    size="small"
                    onClick={() => handleBulkEmail(reg.courseCategory)}
                    title={`Email all ${reg.courseCategory} students`}
                    sx={{ p: 0.5 }}
                  >
                    <Mail fontSize="small" />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell>
                <Tooltip title={reg.subCategory} arrow>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                    {reg.subCategory}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Chip 
                  label={reg.studyLevel} 
                  size="small" 
                  color="secondary" 
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
              </TableCell>
              <TableCell>
                {reg.studySubLevel && (
                  <Chip 
                    label={reg.studySubLevel.length > 12 ? reg.studySubLevel.substring(0, 12) + '...' : reg.studySubLevel} 
                    size="small" 
                    color="default" 
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 24, maxWidth: 100 }}
                  />
                )}
              </TableCell>
              <TableCell>
                <Chip 
                  label={reg.studyMode} 
                  size="small" 
                  color="info" 
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
              </TableCell>
              <TableCell>
                <Chip
                  icon={reg.paymentStatus === 'paid' ? <CheckCircle fontSize="small" /> : null}
                  label={reg.paymentStatus === 'paid' ? "Paid" : "Unpaid"}
                  color={reg.paymentStatus === 'paid' ? "success" : "warning"}
                  size="small"
                  variant="outlined"
                  onClick={() => updatePaymentStatus(reg._id, reg.paymentStatus === 'paid' ? 'unpaid' : 'paid')}
                  sx={{ cursor: 'pointer', fontSize: '0.7rem', height: 24 }}
                />
              </TableCell>
              <TableCell>
                <Chip
                  icon={reg.attendanceStatus === 'attended' ? <CheckCircle fontSize="small" /> : null}
                  label={reg.attendanceStatus === 'attended' ? "Attended" : "Not Attended"}
                  color={reg.attendanceStatus === 'attended' ? "success" : "default"}
                  size="small"
                  variant="outlined"
                  onClick={() => updateAttendanceStatus(reg._id, reg.attendanceStatus === 'attended' ? 'not_attended' : 'attended')}
                  sx={{ cursor: 'pointer', fontSize: '0.7rem', height: 24 }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  {reg.registeredBy ? `${reg.registeredBy.firstName} ${reg.registeredBy.lastName}` : 'Direct/Web'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  {new Date(reg.createdAt).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {new Date(reg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </TableCell>
              <TableCell>
                {reg.lastUpdated && new Date(reg.lastUpdated) > new Date(reg.createdAt) ? (
                  <Tooltip title={`Updated: ${new Date(reg.lastUpdated).toLocaleString()}`} arrow>
                    <Chip
                      icon={<Refresh fontSize="small" />}
                      label="Yes"
                      color="warning"
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 24 }}
                    />
                  </Tooltip>
                ) : (
                  <Chip
                    label="No"
                    color="default"
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 24 }}
                  />
                )}
              </TableCell>
              <TableCell>
                <Chip
                  icon={reg.emailSent ? <CheckCircle fontSize="small" /> : <Error fontSize="small" />}
                  label={reg.emailSent ? "Sent" : "Failed"}
                  color={reg.emailSent ? "success" : "error"}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuClick(e, reg)}
                  sx={{ p: 0.5 }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {filteredRegistrations.length === 0 && (
            <TableRow>
              <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {loading ? 'Loading...' : 'No registrations found'}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredRegistrations.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          '& .MuiTablePagination-toolbar': {
            px: { xs: 1, sm: 2 },
            py: 1,
            minHeight: 48,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            rowGap: 0.5,
          },
          '& .MuiTablePagination-spacer': {
            display: { xs: 'none', sm: 'block' },
          },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
          },
        }}
      />
    </TableContainer>
  );

  // Render action menu
  const renderActionMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
    >
      <MuiMenuItem onClick={() => {
        if (selectedStudentForActions) {
          handleViewStudent(selectedStudentForActions);
        }
        handleMenuClose();
      }}>
        <ListItemIcon>
          <Visibility fontSize="small" />
        </ListItemIcon>
        <ListItemText>View Details</ListItemText>
      </MuiMenuItem>
      <MuiMenuItem onClick={() => {
        if (selectedStudentForActions) {
          handleEditStudent(selectedStudentForActions);
        }
        handleMenuClose();
      }}>
        <ListItemIcon>
          <Edit fontSize="small" />
        </ListItemIcon>
        <ListItemText>Edit Registration</ListItemText>
      </MuiMenuItem>
      <MuiMenuItem onClick={() => {
        if (selectedStudentForActions) {
          updatePaymentStatus(selectedStudentForActions._id, selectedStudentForActions.paymentStatus === 'paid' ? 'unpaid' : 'paid');
        }
        handleMenuClose();
      }}>
        <ListItemIcon>
          <CheckCircle fontSize="small" />
        </ListItemIcon>
        <ListItemText>Mark as {selectedStudentForActions?.paymentStatus === 'paid' ? 'Unpaid' : 'Paid'}</ListItemText>
      </MuiMenuItem>
      <MuiMenuItem onClick={() => {
        if (selectedStudentForActions) {
          updateAttendanceStatus(selectedStudentForActions._id, selectedStudentForActions.attendanceStatus === 'attended' ? 'not_attended' : 'attended');
        }
        handleMenuClose();
      }}>
        <ListItemIcon>
          <CheckCircle fontSize="small" />
        </ListItemIcon>
        <ListItemText>Mark as {selectedStudentForActions?.attendanceStatus === 'attended' ? 'Not Attended' : 'Attended'}</ListItemText>
      </MuiMenuItem>
      <MuiMenuItem onClick={() => {
        if (selectedStudentForActions) {
          handleDeleteStudent(selectedStudentForActions);
        }
        handleMenuClose();
      }}>
        <ListItemIcon>
          <Delete fontSize="small" color="error" />
        </ListItemIcon>
        <ListItemText sx={{ color: 'error.main' }}>Delete Registration</ListItemText>
      </MuiMenuItem>
    </Menu>
  );

  // Render view dialog
  const renderViewDialog = () => (
    <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Typography variant="h6">Student Registration Details</Typography>
      </DialogTitle>
      <DialogContent>
        {selectedStudent && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Name</Typography>
              <Typography variant="body1">{selectedStudent.firstName} {selectedStudent.lastName}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Email</Typography>
              <Typography variant="body1">{selectedStudent.email}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
              <Typography variant="body1">{selectedStudent.phone}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Registration ID</Typography>
              <Typography variant="body1">{selectedStudent.registrationId || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Course Category</Typography>
              <Chip label={selectedStudent.courseCategory} size="small" color="primary" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Subcategory</Typography>
              <Typography variant="body1">{selectedStudent.subCategory}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Study Level</Typography>
              <Chip label={selectedStudent.studyLevel} size="small" color="secondary" />
            </Grid>
            {selectedStudent.studySubLevel && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Study Sub-Level</Typography>
                <Chip label={selectedStudent.studySubLevel} size="small" color="default" />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Study Mode</Typography>
              <Chip label={selectedStudent.studyMode} size="small" color="info" />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Experience</Typography>
              <Typography variant="body1">{selectedStudent.experience}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Goals</Typography>
              <Typography variant="body1">{selectedStudent.goals}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">How did they hear about us?</Typography>
              <Typography variant="body1">{selectedStudent.hearAboutUs}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Registration Date</Typography>
              <Typography variant="body1">
                {new Date(selectedStudent.createdAt).toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Email Status</Typography>
              <Chip
                icon={selectedStudent.emailSent ? <CheckCircle /> : <Error />}
                label={selectedStudent.emailSent ? "Email Sent Successfully" : "Email Failed"}
                color={selectedStudent.emailSent ? "success" : "error"}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Status</Typography>
              <Chip
                label={selectedStudent.status || 'pending'}
                color="warning"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Payment Status</Typography>
              <Chip
                icon={selectedStudent.paymentStatus === 'paid' ? <CheckCircle /> : null}
                label={selectedStudent.paymentStatus === 'paid' ? "Paid" : "Unpaid"}
                color={selectedStudent.paymentStatus === 'paid' ? "success" : "warning"}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Attendance Status</Typography>
              <Chip
                icon={selectedStudent.attendanceStatus === 'attended' ? <CheckCircle /> : null}
                label={selectedStudent.attendanceStatus === 'attended' ? "Attended" : "Not Attended"}
                color={selectedStudent.attendanceStatus === 'attended' ? "success" : "default"}
                size="small"
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  // Render delete confirmation dialog
  const renderDeleteDialog = () => (
    <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h6" color="error">Delete Registration</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete the registration for {selectedStudent?.firstName} {selectedStudent?.lastName}? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
        <Button onClick={confirmDelete} color="error" variant="contained">
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Course category data for edit dialog - ALL categories from homepage services
  const editCourseCategories = [
    {
      title: "Technology",
      subCategories: [
        "Artificial Intelligence & Machine Learning",
        "Web Development (Frontend & Backend)",
        "Mobile App Development",
        "Cloud Computing & DevOps",
        "Cybersecurity",
        "Python Programming",
        "Software Development",
        "Database Management",
        "UI/UX Design"
      ]
    },
    {
      title: "Business",
      subCategories: [
        "Business Administration",
        "Entrepreneurship & Startups",
        "Digital Marketing",
        "Financial Management",
        "Project Management",
        "Human Resources",
        "Supply Chain Management",
        "Business Analytics",
        "Strategic Management",
        "International Business",
        "HR Strategy & Workforce Planning",
        "Talent Acquisition & Recruitment",
        "Employee Relations & Engagement",
        "Performance Management Systems",
        "Compensation & Benefits Administration",
        "HR Analytics & Metrics",
        "Training & Development",
        "Organizational Development",
        "Employee Wellness Programs",
        "Labor Law & Compliance",
        "Diversity & Inclusion",
        "HR Information Systems"
      ]
    },
    {
      title: "Finance",
      subCategories: [
        "Financial Accounting & Reporting",
        "Tax Planning & Compliance",
        "Bookkeeping & Financial Analysis",
        "Financial Advisory Services",
        "Auditing & Internal Controls",
        "International Accounting Standards",
        "Corporate Finance",
        "Investment Analysis",
        "Risk Management & Compliance",
        "Forensic Accounting",
        "Tax Advisory & Consulting"
      ]
    },
    {
      title: "Professional Coaching",
      subCategories: [
        "Data Analytics Coaching",
        "CPA (Certified Public Accountant) ICPAR Coaching",
        "CPA (Certified Public Accountant) KASNEB Coaching",
        "ACCA (Association of Chartered Certified Accountants) Coaching",
        "PMP (Project Management Professional) Coaching",
        "Leadership Development Coaching"
      ]
    },
    {
      title: "Career Development",
      subCategories: [
        "Resume & CV Writing",
        "Interview Preparation",
        "Career Planning & Counseling",
        "Job Search Strategies",
        "Networking Skills",
        "Professional Communication",
        "Leadership Development",
        "Public Speaking",
        "Workplace Skills",
        "Career Transition",
        "Career Guidance",
        "Job Exams and Assessments"
      ]
    },
    {
      title: "Academic Coaching",
      subCategories: [
        "Mathematics (All Levels)",
        "Physics & Chemistry",
        "Biology & Life Sciences",
        "English Language & Literature",
        "French & Other Languages",
        "Test Preparation (SAT, GRE, etc.)",
        "Study Skills & Time Management",
        "Research & Writing",
        "Critical Thinking",
        "Academic Planning"
      ]
    },
    {
      title: "Languages",
      subCategories: [
        "Chinese",
        "German",
        "Arabic",
        "Kinyarwanda",
        "French",
        "Kiswahili",
        "English",
        "TOEFL Preparation"
      ]
    },
    {
      title: "Wellness",
      subCategories: [
        "Parenting Skills & Child Development",
        "Mental Health & Wellness Support",
        "Emotional Intelligence Training",
        "Family Counseling & Therapy",
        "Stress Management Techniques",
        "Work-Life Balance Strategies",
        "Child Psychology & Development",
        "Adolescent Mental Health",
        "Relationship Counseling",
        "Self-Care & Mindfulness",
        "Parenting for Special Needs",
        "Family Communication Skills"
      ]
    }
  ];

  // Level/sub-level mapping per sub-category
  const kasnebCpaStudyLevels: Record<string, string[]> = {
    "Foundation Level": [
      "CA11: Financial Accounting",
      "CA12: Communication Skills",
      "CA13: Introduction to Law and Governance",
      "CA14: Economics",
      "CA15: Quantitative Analysis",
      "CA16: Information Communication Technology"
    ],
    "Intermediate Level": [
      "CA21: Company Law",
      "CA22: Financial Management",
      "CA23: Financial Reporting and Analysis",
      "CA24: Auditing and Assurance",
      "CA25: Management Accounting",
      "CA26: Public Finance and Taxation"
    ],
    "Advanced Level - Compulsory Papers": [
      "CA31: Leadership and Management",
      "CA32: Advanced Financial Reporting and Analysis",
      "CA33: Advanced Financial Management",
      "CA34S3: Advanced Management Accounting"
    ],
    "Advanced Level - Specialisation Papers": [
      "CA34S1: Advanced Taxation",
      "CA34S2: Advanced Auditing and Assurance",
      "CA34S4: Advanced Public Financial Management"
    ],
    "Advanced Level - Practical / Professional Components": [
      "CA35P: Business Data Analytics (Practical Paper)",
      "CA36WE: Workshop on Ethics",
      "CA37WP: Workshop on Work Simulation"
    ]
  };

  const icparCpaStudyLevels: Record<string, string[]> = {
    "Technical Level": [
      "Financial Accounting",
      "Management Accounting",
      "Taxation",
      "Audit & Assurance"
    ],
    "Operational Level": [
      "Law, Ethics & Governance",
      "Digital Finance",
      "Financial Management",
      "Financial Reporting",
      "Advanced Taxation"
    ],
    "Strategic Level": [
      "Advanced Audit & Assurance",
      "Strategic Management",
      "Advanced Financial Reporting",
      "Advanced Public Financial Management (public sector pathway)",
      "Managing Performance in the Public Sector (public sector pathway)",
      "Managing Business Performance (private sector pathway)",
      "Advanced Financial Management (private sector pathway)"
    ],
    "Professional Level": [
      "Test of Professional Competence",
      "Choose Private Sector",
      "Choose Public Sector"
    ]
  };

  const standardStudyLevels = ['Beginner', 'Intermediate', 'Advanced', 'Professional'];

  const getEditStudyLevels = (): Record<string, string[]> | string[] => {
    const subCat = editFormData.subCategory || '';
    if (subCat.includes('KASNEB')) return kasnebCpaStudyLevels;
    if (subCat.includes('ICPAR')) return icparCpaStudyLevels;
    if (subCat.includes('CPA')) return icparCpaStudyLevels;
    return standardStudyLevels;
  };

  const editStudyLevels = getEditStudyLevels();
  const editLevelIsMap = !Array.isArray(editStudyLevels);
  const editLevelKeys = editLevelIsMap ? Object.keys(editStudyLevels as Record<string, string[]>) : (editStudyLevels as string[]);
  // Get sub-levels for non-CPA courses (when studyLevel is a string)
  const editSubLevels: string[] = editLevelIsMap && editFormData.studyLevel && !Array.isArray(editFormData.studyLevel)
    ? (editStudyLevels as Record<string, string[]>)[editFormData.studyLevel] || []
    : [];

  // Render edit dialog
  const renderEditDialog = () => (
    <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
        <Typography variant="h6" sx={{ fontSize: 'inherit' }}>Edit Registration - {selectedStudent?.firstName} {selectedStudent?.lastName}</Typography>
      </DialogTitle>
      <DialogContent sx={{ maxHeight: { xs: 'none', sm: '70vh' }, overflowY: 'auto' }}>
        <Grid container spacing={2} sx={{ mt: 0 }}>

          {/* Course Category */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Course Category</InputLabel>
              <Select
                value={editFormData.courseCategory || ''}
                onChange={(e) => {
                  const newCategory = e.target.value;
                  setEditFormData({
                    ...editFormData,
                    courseCategory: newCategory,
                    subCategory: '',
                    studyLevel: '',
                    studySubLevel: ''
                  });
                }}
                label="Course Category"
              >
                {editCourseCategories.map(cat => (
                  <MenuItem key={cat.title} value={cat.title}>{cat.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Sub-Category — filtered by selected category */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Sub-Category</InputLabel>
              <Select
                value={editFormData.subCategory || ''}
                onChange={(e) => {
                  const newSubCategory = e.target.value;
                  const isCPACourse = newSubCategory.includes('CPA') || newSubCategory.includes('KASNEB') || newSubCategory.includes('ICPAR');
                  setEditFormData({
                    ...editFormData,
                    subCategory: newSubCategory,
                    studyLevel: isCPACourse ? [] : '',
                    studySubLevel: isCPACourse ? [] : ''
                  });
                }}
                label="Sub-Category"
                disabled={!editFormData.courseCategory}
              >
                <MenuItem value=""><em>Select sub-category</em></MenuItem>
                {(editCourseCategories.find(c => c.title === editFormData.courseCategory)?.subCategories || []).map(sub => (
                  <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Study Level & Sub-Level — CPA courses use multi-select checkboxes, others use dropdown */}
          {(() => {
            const isKasnebCpa = editFormData.subCategory?.includes('KASNEB');
            const isIcparCpa = editFormData.subCategory?.includes('ICPAR');
            const isCPACourse = isKasnebCpa || isIcparCpa || editFormData.subCategory?.includes('CPA');

            if (isCPACourse) {
              // CPA courses: Show multi-select checkboxes for levels and sub-levels
              const levelData = isKasnebCpa ? kasnebCpaStudyLevels : icparCpaStudyLevels;
              return (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                      Select Study Levels & Papers (Multiple Allowed)
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {Object.entries(levelData).map(([level, subLevels]) => (
                        <Box key={level} sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          bgcolor: 'background.paper'
                        }}>
                          {/* Level Checkbox */}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Checkbox
                              checked={Array.isArray(editFormData.studyLevel) && editFormData.studyLevel.includes(level)}
                              onChange={() => handleArrayToggle('studyLevel', level)}
                              size="small"
                            />
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {level}
                            </Typography>
                          </Box>
                          {/* Sub-Level Checkboxes */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 4 }}>
                            {subLevels.map(subLevel => (
                              <Box
                                key={subLevel}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: Array.isArray(editFormData.studySubLevel) && editFormData.studySubLevel.includes(subLevel)
                                    ? 'primary.main'
                                    : 'divider',
                                  bgcolor: Array.isArray(editFormData.studySubLevel) && editFormData.studySubLevel.includes(subLevel)
                                    ? 'primary.50'
                                    : 'background.default',
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                                onClick={() => handleArrayToggle('studySubLevel', subLevel)}
                              >
                                <Checkbox
                                  checked={Array.isArray(editFormData.studySubLevel) && editFormData.studySubLevel.includes(subLevel)}
                                  onChange={() => handleArrayToggle('studySubLevel', subLevel)}
                                  size="small"
                                  sx={{ p: 0, mr: 0.5 }}
                                />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {subLevel}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                </>
              );
            } else {
              // Non-CPA courses: Use dropdown selects
              return (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Study Level</InputLabel>
                      <Select
                        value={editFormData.studyLevel || ''}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          studyLevel: e.target.value,
                          studySubLevel: ''
                        })}
                        label="Study Level"
                        disabled={!editFormData.subCategory}
                      >
                        <MenuItem value=""><em>Select level</em></MenuItem>
                        {editLevelKeys.map(level => (
                          <MenuItem key={level} value={level}>{level}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Study Sub-Level</InputLabel>
                      <Select
                        value={editFormData.studySubLevel || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, studySubLevel: e.target.value })}
                        label="Study Sub-Level"
                        disabled={editLevelIsMap ? !editFormData.studyLevel : false}
                      >
                        <MenuItem value="">None</MenuItem>
                        {editLevelIsMap
                          ? editSubLevels.map(sub => (
                              <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                            ))
                          : availableStudySubLevels.map(sub => (
                              <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                            ))
                        }
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              );
            }
          })()}

          {/* Study Mode */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Study Mode</InputLabel>
              <Select
                value={editFormData.studyMode || ''}
                onChange={(e) => setEditFormData({ ...editFormData, studyMode: e.target.value })}
                label="Study Mode"
              >
                <MenuItem value="Online">Online</MenuItem>
                <MenuItem value="Offline/In-Person">Offline/In-Person</MenuItem>
                <MenuItem value="Hybrid">Hybrid</MenuItem>
                <MenuItem value="Self-Paced">Self-Paced</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Payment Status */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={editFormData.paymentStatus || ''}
                onChange={(e) => setEditFormData({ ...editFormData, paymentStatus: e.target.value })}
                label="Payment Status"
              >
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="unpaid">Unpaid</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Attendance Status */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Attendance Status</InputLabel>
              <Select
                value={editFormData.attendanceStatus || ''}
                onChange={(e) => setEditFormData({ ...editFormData, attendanceStatus: e.target.value })}
                label="Attendance Status"
              >
                <MenuItem value="attended">Attended</MenuItem>
                <MenuItem value="not_attended">Not Attended</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Registration Status */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Registration Status</InputLabel>
              <Select
                value={editFormData.status || ''}
                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                label="Registration Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="started_attending">Started Attending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>

        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleSaveEdit} variant="contained" disabled={savingEdit}>
          {savingEdit ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Render bulk email dialog
  const renderBulkEmailDialog = () => {
    const studentsInCategory = registrations.filter(reg => reg.courseCategory === selectedCourseCategory);
    
    // Filter students based on search query and study level/sub-level filters
    const filteredStudents = studentsInCategory.filter(student => {
      const searchLower = bulkEmailSearchQuery.toLowerCase();
      const matchesSearch = (
        student.firstName.toLowerCase().includes(searchLower) ||
        student.lastName.toLowerCase().includes(searchLower) ||
        student.email.toLowerCase().includes(searchLower) ||
        student.subCategory.toLowerCase().includes(searchLower)
      );
      
      const matchesStudyLevel = !bulkEmailFilterStudyLevel || student.studyLevel === bulkEmailFilterStudyLevel;
      const matchesStudySubLevel = !bulkEmailFilterStudySubLevel || student.studySubLevel === bulkEmailFilterStudySubLevel;
      
      return matchesSearch && matchesStudyLevel && matchesStudySubLevel;
    });
    
    const allSelected = filteredStudents.length > 0 && filteredStudents.every(student => selectedStudentsForBulkEmail.includes(student._id));
    const someSelected = filteredStudents.some(student => selectedStudentsForBulkEmail.includes(student._id));
    
    return (
    <Dialog open={bulkEmailDialogOpen} onClose={() => {
      setBulkEmailDialogOpen(false);
      setBulkEmailSearchQuery('');
      setBulkEmailFilterStudyLevel('');
      setBulkEmailFilterStudySubLevel('');
    }} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
        <Typography variant="h6" sx={{ fontSize: 'inherit' }}>Send Bulk Email to {selectedCourseCategory} Students</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select the students you want to send this email to from the {selectedCourseCategory} category.
        </Typography>
        
        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search by name, email, or subcategory..."
          value={bulkEmailSearchQuery}
          onChange={(e) => setBulkEmailSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
        
        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Study Level</InputLabel>
              <Select
                value={bulkEmailFilterStudyLevel}
                onChange={(e) => setBulkEmailFilterStudyLevel(e.target.value)}
                label="Study Level"
              >
                <MenuItem value="">All Levels</MenuItem>
                {Array.from(new Set(studentsInCategory.map(s => s.studyLevel))).map(level => (
                  <MenuItem key={level} value={level}>{level}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Study Sub-Level</InputLabel>
              <Select
                value={bulkEmailFilterStudySubLevel}
                onChange={(e) => setBulkEmailFilterStudySubLevel(e.target.value)}
                label="Study Sub-Level"
              >
                <MenuItem value="">All Sub-Levels</MenuItem>
                {Array.from(new Set(studentsInCategory.map(s => s.studySubLevel).filter(Boolean))).map(subLevel => (
                  <MenuItem key={subLevel} value={subLevel}>{subLevel}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        {/* Student Selection */}
        <Box sx={{ 
          mb: 2, 
          maxHeight: 300, 
          overflow: 'auto', 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper'
        }}>
          <Box sx={{ 
            position: 'sticky', 
            top: 0, 
            bgcolor: 'background.paper', 
            p: 2, 
            borderBottom: '1px solid',
            borderColor: 'divider',
            zIndex: 1 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStudentsForBulkEmail(prev => [
                        ...prev.filter(id => !studentsInCategory.map(s => s._id).includes(id)),
                        ...filteredStudents.map(reg => reg._id)
                      ]);
                    } else {
                      setSelectedStudentsForBulkEmail(prev => prev.filter(id => !filteredStudents.map(s => s._id).includes(id)));
                    }
                  }}
                />
                <Typography variant="subtitle2">
                  {selectedStudentsForBulkEmail.filter(id => filteredStudents.map(s => s._id).includes(id)).length} of {filteredStudents.length} selected
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {filteredStudents.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No students found matching your search.
              </Typography>
            </Box>
          ) : (
            filteredStudents.map((student) => (
              <Box
                key={student._id}
                sx={{
                  p: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Checkbox
                  checked={selectedStudentsForBulkEmail.includes(student._id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStudentsForBulkEmail(prev => [...prev, student._id]);
                    } else {
                      setSelectedStudentsForBulkEmail(prev => prev.filter(id => id !== student._id));
                    }
                  }}
                />
                <Box sx={{ ml: 2, flex: 1 }}>
                  <Typography variant="body2" fontWeight="medium">
                    {student.firstName} {student.lastName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {student.email} • {student.subCategory}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>
        
        <TextField
          fullWidth
          label="Subject"
          value={bulkEmailSubject}
          onChange={(e) => setBulkEmailSubject(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Message"
          multiline
          rows={6}
          value={bulkEmailMessage}
          onChange={(e) => setBulkEmailMessage(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Attachments</Typography>
          <input
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            style={{ display: 'none' }}
            id="bulk-email-attachment-input"
            onChange={handleBulkEmailAttachmentChange}
          />
          <label htmlFor="bulk-email-attachment-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUpload />}
            >
              Upload Attachments
            </Button>
          </label>
          {bulkEmailAttachments.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {bulkEmailAttachments.map((file, index) => (
                <Chip
                  key={index}
                  label={file.name}
                  onDelete={() => handleRemoveBulkEmailAttachment(index)}
                  icon={<AttachFile />}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => {
          setBulkEmailDialogOpen(false);
          setBulkEmailSearchQuery('');
          setBulkEmailFilterStudyLevel('');
          setBulkEmailFilterStudySubLevel('');
        }} disabled={sendingEmail}>Cancel</Button>
        <Button onClick={sendBulkEmail} variant="contained" startIcon={<Send />} disabled={sendingEmail || selectedStudentsForBulkEmail.length === 0}>
          {sendingEmail ? 'Sending...' : `Send to ${selectedStudentsForBulkEmail.length} Student${selectedStudentsForBulkEmail.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
    );
  };

  // Render individual email dialog
  const renderEmailDialog = () => (
    <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Typography variant="h6">Send Email to {selectedStudentForEmail?.firstName} {selectedStudentForEmail?.lastName}</Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Subject"
          value={emailSubject}
          onChange={(e) => setEmailSubject(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Message"
          multiline
          rows={6}
          value={emailMessage}
          onChange={(e) => setEmailMessage(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Attachments</Typography>
          <input
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            style={{ display: 'none' }}
            id="email-attachment-input"
            onChange={handleEmailAttachmentChange}
          />
          <label htmlFor="email-attachment-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUpload />}
            >
              Upload Attachments
            </Button>
          </label>
          {emailAttachments.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {emailAttachments.map((file, index) => (
                <Chip
                  key={index}
                  label={file.name}
                  onDelete={() => handleRemoveEmailAttachment(index)}
                  icon={<AttachFile />}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEmailDialogOpen(false)} disabled={sendingEmail}>Cancel</Button>
        <Button onClick={sendIndividualEmail} variant="contained" startIcon={<Send />} disabled={sendingEmail}>
          {sendingEmail ? 'Sending...' : 'Send Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          mb: { xs: 2.5, sm: 4 },
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 2,
          p: { xs: 2, sm: 3 },
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{ fontWeight: 'bold', fontSize: { xs: '1.5rem', sm: '2.25rem', md: '3rem' } }}
        >
          Student Registration Management
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 300, fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
          View and manage student registrations
        </Typography>
      </Box>

      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: { xs: 40, sm: 48 }, '& .MuiTab-root': { minHeight: { xs: 40, sm: 48 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } } }}
        >
          <Tab label="Registered Students" />
        </Tabs>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {renderStatistics()}
      {renderFilters()}
      {renderStudentTable()}
      {renderActionMenu()}
      {renderViewDialog()}
      {renderEditDialog()}
      {renderDeleteDialog()}
      {renderBulkEmailDialog()}
      {renderEmailDialog()}

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }} icon={<CheckCircle />}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={8000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default StudentRegistrationPage;
